import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  Users, 
  Filter,
  CheckCircle,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimesheetCalendar = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (user?.role !== 'field_employee') {
      fetchCalendarData();
      fetchEmployees();
      fetchProjects();
    }
  }, [user, currentDate, selectedEmployee, selectedProject]);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const params = { year, month };
      if (selectedEmployee) params.employee_id = selectedEmployee;
      if (selectedProject) params.project_id = selectedProject;

      const response = await axios.get(`${API}/timesheets/calendar`, { params });
      setTimesheetEntries(response.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      toast.error('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      const fieldEmployees = response.data.filter(u => 
        u.role === 'field_employee' || u.role === 'foreman'
      );
      setAllEmployees(fieldEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setAllProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getEntriesForDate = (date) => {
    if (!date) return [];
    const dateString = date.toISOString().split('T')[0];
    return timesheetEntries.filter(entry => entry.work_date === dateString);
  };

  const getTotalHoursForDate = (date) => {
    const entries = getEntriesForDate(date);
    return entries.reduce((total, entry) => total + entry.total_hours, 0);
  };

  const getUniqueEmployeesForDate = (date) => {
    const entries = getEntriesForDate(date);
    const uniqueEmployees = [...new Set(entries.map(entry => entry.employee_name))];
    return uniqueEmployees.length;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedEntries([]);
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setSelectedEntries(getEntriesForDate(date));
  };

  const approveEntry = async (entryId) => {
    try {
      await axios.post(`${API}/timesheets/${entryId}/approve`);
      toast.success('Timesheet entry approved');
      fetchCalendarData(); // Refresh data
    } catch (error) {
      console.error('Error approving entry:', error);
      toast.error('Failed to approve timesheet entry');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (user?.role === 'field_employee') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Calendar className="h-12 w-12 text-slate-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Manager Access Only</h2>
          <p className="text-slate-500">Calendar view is for managers and admins</p>
        </div>
      </div>
    );
  }

  const days = getDaysInMonth(currentDate);

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Timesheet Calendar</h1>
        <p className="text-slate-500">Track employee hours and project progress</p>
      </div>

      {/* Filters */}
      <Card className="card mb-6">
        <CardHeader className="card-header">
          <CardTitle className="text-slate-900 flex items-center">
            <Filter className="h-5 w-5 mr-2 text-blue-500" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="card-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700">Filter by Employee</Label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-md text-slate-900"
              >
                <option value="">All Employees</option>
                {allEmployees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.full_name} ({employee.role.replace('_', ' ')})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="text-slate-700">Filter by Project</Label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-md text-slate-900"
              >
                <option value="">All Projects</option>
                {allProjects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title} - {project.client_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card className="card">
            <CardHeader className="card-header">
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-900 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-500" />
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </CardTitle>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => navigateMonth(-1)}
                    size="sm"
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => navigateMonth(1)}
                    size="sm"
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="card-body">
              {loading ? (
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 text-slate-500 mx-auto mb-4 animate-spin" />
                  <p className="text-slate-500">Loading calendar...</p>
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {/* Day headers */}
                  {dayNames.map(day => (
                    <div key={day} className="p-2 text-center text-slate-500 font-medium text-sm">
                      {day}
                    </div>
                  ))}
                  
                  {/* Calendar days */}
                  {days.map((day, index) => {
                    if (!day) {
                      return <div key={index} className="p-2 h-20"></div>;
                    }
                    
                    const entries = getEntriesForDate(day);
                    const totalHours = getTotalHoursForDate(day);
                    const employeeCount = getUniqueEmployeesForDate(day);
                    const isToday = day.toDateString() === new Date().toDateString();
                    const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                    
                    return (
                      <div
                        key={index}
                        onClick={() => handleDateClick(day)}
                        className={`p-2 h-20 border border-slate-300 rounded-lg cursor-pointer transition-colors hover:bg-slate-100 ${
                          isToday ? 'border-blue-500 bg-blue-50' : ''
                        } ${
                          isSelected ? 'border-green-500 bg-green-50' : ''
                        }`}
                      >
                        <div className="text-sm text-slate-900 font-medium mb-1">
                          {day.getDate()}
                        </div>
                        
                        {entries.length > 0 && (
                          <div className="space-y-1">
                            <div className="text-xs text-green-700 font-mono">
                              {totalHours.toFixed(1)}h
                            </div>
                            <div className="text-xs text-slate-500 flex items-center">
                              <Users className="h-3 w-3 mr-1" />
                              {employeeCount}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Selected Date Details */}
        <div>
          <Card className="card">
            <CardHeader className="card-header">
              <CardTitle className="text-slate-900 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-blue-500" />
                {selectedDate ? formatDate(selectedDate) : 'Select a Date'}
              </CardTitle>
            </CardHeader>
            <CardContent className="card-body">
              {selectedEntries.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500">
                    {selectedDate ? 'No timesheet entries' : 'Click a date to view entries'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <div className="text-sm text-slate-700 mb-2">Day Summary</div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-slate-500">Total Hours</div>
                        <div className="text-green-700 font-mono">
                          {selectedEntries.reduce((sum, entry) => sum + entry.total_hours, 0).toFixed(1)}h
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500">Total Pay</div>
                        <div className="text-green-700 font-mono">
                          {formatCurrency(selectedEntries.reduce((sum, entry) => sum + entry.total_pay, 0))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Individual Entries */}
                  <div className="space-y-3">
                    {selectedEntries.map((entry) => (
                      <div key={entry.id} className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-medium text-slate-900 text-sm">{entry.employee_name}</h4>
                            <p className="text-xs text-slate-500">{entry.project_title}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-green-700 font-mono text-sm">
                              {entry.total_hours}h
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatCurrency(entry.total_pay)}
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-xs text-slate-700 mb-2">{entry.work_description}</p>
                        
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-slate-500">
                            R: {entry.regular_hours}h | OT: {entry.overtime_hours}h | DT: {entry.double_time_hours}h
                          </div>
                          
                          {!entry.supervisor_approved && (
                            <Button
                              onClick={() => approveEntry(entry.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                          )}
                          
                          {entry.supervisor_approved && (
                            <div className="text-xs text-green-700 flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approved
                            </div>
                          )}
                        </div>
                        
                        {entry.location && (
                          <div className="text-xs text-slate-500 mt-1">
                            📍 {entry.location}
                          </div>
                        )}
                        
                        {entry.weather_conditions && (
                          <div className="text-xs text-slate-500">
                            🌤️ {entry.weather_conditions}
                          </div>
                        )}
                        
                        {entry.equipment_used && entry.equipment_used.length > 0 && (
                          <div className="text-xs text-slate-500 mt-1">
                            🔧 {entry.equipment_used.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TimesheetCalendar;